import styled from '@emotion/styled';
import { Card, CardContent, CardMedia, CircularProgress, Drawer, Grid, Pagination, Typography, useMediaQuery } from '@mui/material';
import React, { useEffect, useState } from 'react';
import { GetAllApplications, GetUsersIdentity } from '../../services/ApiService';
import { useTranslation } from 'react-i18next';
import { useStyle } from '../../styles/style';
import clsx from 'clsx';
import { EditApplicationCard } from '../EditCard/EditApplicationCard';
import { Err } from '../Error/Err';
import i18n from '../../i18n/i18n';
import { Button } from '../Button/button';
import { getApiConfig, getIdToken, msalResult } from '../../authConfig';
import useToggle from '../../hooks/useToggle';
import { 
  DataState, 
  DataError,
  createLoadingState, 
  createErrorState, 
  createLoadedState, 
  isLoading, 
  isError, 
  isLoaded 
} from '../../utils/dataUtils';
import { Application, SecurityGroup, ApiResponse } from '../../utils/types';

const ApplicationCard = () => {
  // Use the DataState type to manage application data
  const [applicationState, setApplicationState] = useState<DataState<Application[]>>(createLoadingState<Application[]>());
  
  // Boolean state variables with useToggle
  const [isAdmin, toggleAdmin] = useToggle(false);
  const [createNewApp, toggleCreateNewApp] = useToggle(false);
  const [openDrawer, toggleDrawer] = useToggle(false);
  
  // Other state variables
  const [selectedCardIndex, setSelectedCardIndex] = useState<number | null>(null);
  const [isHoveredPL, setIsHoveredPL] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [cardDetails, setCardDetails] = useState<Application | null>(null);
  
  // Configuration and utilities
  const matches = useMediaQuery('(max-width:600px)');
  const matchesTab = useMediaQuery('(max-width:768px)');
  const perPage = 15;
  const [securityGrp, setSecurityGrp] = useState<SecurityGroup | null>(null);
  const { theme } = msalResult;
  const { classes } = useStyle();
  const { t } = useTranslation();

  /**
   * Ensures the API configuration is properly loaded
   */
  const ensureApiConfig = async (): Promise<boolean> => {
    if (msalResult.apim?.apiURL) {
      return true;
    }
    
    try {
      const currentUrl = window.location.href;
      const apiConfig = await getApiConfig(currentUrl);
      
      if (!apiConfig || !apiConfig.apiURL) {
        return false;
      }
      
      msalResult.apim = apiConfig;
      return true;
    } catch (error) {
      return false;
    }
  };

  /**
   * Fetches application data from the API
   */
  const fetchAppData = async () => {
    setApplicationState(createLoadingState<Application[]>());
    
    try {
      // First ensure API config is available
      const configOk = await ensureApiConfig();
      if (!configOk) {
        setApplicationState(createErrorState<Application[]>(
          { message: "API configuration could not be initialized" }
        ));
        return;
      }
      
      const tokenAccess = await getIdToken();
      const res = await GetAllApplications(tokenAccess) as ApiResponse<Application[]> | 401;
      
      if (res === 401) {
        const authError: DataError = {
          message: "Authentication failed. Please log in again.", 
          statusCode: 401 
        };
        setApplicationState(createErrorState<Application[]>(authError));
        return;
      }
      
      // Validate the returned data
      if (!res?.data || !Array.isArray(res.data)) {
        setApplicationState(createErrorState<Application[]>(
          { message: "Invalid data format returned from API" }
        ));
        return;
      }
      
      // Check if the data is empty
      if (res.data.length === 0) {
        setApplicationState(createLoadedState([]));
        return;
      }
      
      // Validate application objects to ensure they have required fields
      const validatedData = res.data.map((app: any, index: number) => {
        // Return a validated application with defaults for missing fields
        return {
          applicationId: app.applicationId || `unknown-${index}`,
          name: app.name || 'Unknown Application',
          displayName: app.displayName || app.name || 'Unknown Application',
          typeAbbreviation: app.typeAbbreviation || 'APP',
          typeName: app.typeName || 'Application',
          typeColorNormal: app.typeColorNormal || '#3f51b5',
          typeColorHighlite: app.typeColorHighlite || '#6573c3',
          url: app.url || '',
          imageUrl: app.imageUrl || '',
          iconUrl: app.iconUrl || '',
          ...app
        };
      });
      
      setApplicationState(createLoadedState(validatedData));
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : "Failed to load applications";
      const appError: DataError = {
        message: errorMessage,
        statusCode: undefined
      };
      setApplicationState(createErrorState<Application[]>(appError));
    }
  };

  /**
   * Fetches and sets user profile information
   */
  const fetchUserProfile = async () => {
    try {
      const tokenAccess = await getIdToken();
      const res = await GetUsersIdentity(tokenAccess) as ApiResponse<SecurityGroup>;
      
      if (res && res.data) {
        setSecurityGrp(res.data);
        
        // Set language based on user preference
        if (res.data.preferredLanguage === 'en-GB') {
          i18n.changeLanguage('en-GB');
        } else if (res.data.preferredLanguage === 'sv') {
          i18n.changeLanguage('se');
        } else if (res.data.preferredLanguage === 'no') {
          i18n.changeLanguage('no');
        }

        // Set admin status based on the user's security group
        // Since useToggle is just a toggle, we need to check if the current state
        // matches what we want - if not, toggle it
        const shouldBeAdmin = res.data.isAdmin === true;
        if (shouldBeAdmin !== isAdmin) {
          toggleAdmin();
        }
        
        return res.data;
      }
    } catch (error) {
      // Ensure user is not admin in case of error
      if (isAdmin) {
        toggleAdmin();
      }
    }
    
    return null;
  }

  // Initial data loading
  useEffect(() => { 
    const initialize = async () => {
      // Try to ensure we have the API config
      const configOk = await ensureApiConfig();
      
      if (!configOk) {
        setApplicationState(createErrorState<Application[]>(
          { message: "API configuration not initialized" }
        ));
        return;
      }
      
      // Now that we have confirmed the API config, fetch data
      fetchAppData();
      fetchUserProfile();
    };
    
    initialize();
  }, []);

  // Helper function to get application details safely
  const getApplicationDetails = (): Application[] => {
    if (isLoaded(applicationState)) {
      return applicationState.data;
    }
    return [];
  };

  const applicationDetails = getApplicationDetails();

  // Handle error reporting from child components
  const handleError = (hasError: boolean) => {
    if (hasError) {
      setApplicationState(createErrorState<Application[]>(
        { message: "Application error occurred" }
      ));
    }
  }

  // Calculate pagination
  const pageCount = Array.isArray(applicationDetails) && applicationDetails.length > 0 
    ? Math.ceil(applicationDetails.length / perPage) 
    : 0;

  const calculatePaginatedData = (): Application[] => {
    if (!Array.isArray(applicationDetails) || applicationDetails.length === 0) {
      return [];
    }
    
    const start = (currentPage - 1) * perPage;
    const end = start + perPage;
    
    return applicationDetails.slice(start, end);
  };
  
  const paginatedData = calculatePaginatedData();

  // Event handlers
  const handlePageChange = (_event: React.ChangeEvent<unknown>, value: number) => {
    setCurrentPage(value);
  };

  const handleHover = (hoverState: boolean, type: string, id: string, index: number) => { 
    setIsHoveredPL(false);
    setSelectedCardIndex(index);
  };

  const handleHoverOver = (hoverState: boolean, type: string, id: string, index: number) => {
    setIsHoveredPL(false);
    setSelectedCardIndex(null);
  }

  const handleAddApplication = () => {
    toggleDrawer();
    toggleCreateNewApp();
    setCardDetails(null);
  };

  const handleOpenEditApplication = (application: Application) => {
    setCardDetails(application);
    toggleDrawer();
  };
  
  const handleCloseEdit = () => { 
    toggleDrawer();
    setCardDetails(null);
    if (paginatedData && paginatedData.length === 0) {
      setCurrentPage(Math.max(1, currentPage - 1));
    }
  }

  const handleReload = () => {
    fetchAppData();
  }

  const handleDrawerClose = (event: React.KeyboardEvent | React.MouseEvent) => {
    if (event && event.type === 'keydown' && ((event as React.KeyboardEvent).key === 'Tab' || (event as React.KeyboardEvent).key === 'Shift')) {
      return;
    }
    toggleDrawer();
    toggleCreateNewApp();
  };

  // Styled components
  const AppCard = styled(Card)(
    () => ({
      maxWidth: 300,
      width: 215,
      height: 230,
      borderRadius: 10,
      cursor: "pointer",
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'space-between',
      transition: 'all 0.25s ease-in-out',
      boxShadow: '0 4px 10px rgba(0, 0, 0, 0.05)',
      border: '1px solid rgba(0, 0, 0, 0.05)',
      overflow: 'hidden',
      
      '&:hover': {
        transform: 'translateY(-3px)',
        boxShadow: '0 8px 20px rgba(0, 0, 0, 0.1)',
      },
      '@media(max-width: 600px)': {
        width: '200px'
      },
      '@media(max-width: 500px)': {
        width: '170px'
      }
    })
  );
  
  const AppImage = styled(CardMedia)(() => ({
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      zIndex: 1,
      height: '152px',
      borderRadius: '10px 10px 0 0',
      background: 'linear-gradient(rgba(0,0,0,0.03), rgba(0,0,0,0.05))'
    },
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'cover',
      transition: 'transform 0.3s ease'
    },
    height: '100px',
    '@media(max-width: 600px)': {
      height: '100px'
    },
    '@media(max-width: 500px)': {
      height: '50px',
    }
  }));

  const AppImage1 = styled(CardMedia)(() => ({
    backgroundSize: 'cover',
    backgroundPosition: 'center',
    '& img': {
      width: '100%',
      height: '100%',
      objectFit: 'contain',
      transition: 'transform 0.3s ease'
    },
    height: '130px',
    marginTop: 'auto',
    borderTopLeftRadius: '10px',
    borderTopRightRadius: '10px',
    overflow: 'hidden'
  }));
  
  const AppCardContent = styled(CardContent)({
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: "#FFFFFF",
    paddingBottom: '0px !important',
    textAlign: 'start',
    padding: '8px 12px',
    height: '90px',
    borderBottomLeftRadius: '10px',
    borderBottomRightRadius: '10px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.04)'
  });
  
  const AppTitle = styled(Typography)({
    font: `normal normal bold 16px/24px ${theme?.Style?.font || 'Arial, sans-serif'}`,
    textTransform: 'capitalize',
    color: '#0C1732',
    marginBottom: '4px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '100%',
    letterSpacing: '0.01em',
    '@media(max-width: 600px)': {
      font: `normal normal bold 16px/16px ${theme?.Style?.font || 'Arial, sans-serif'}`,
    },
    '@media(max-width: 500px)': {
      font: `normal normal bold 16px/16px ${theme?.Style?.font || 'Arial, sans-serif'}`,
    },
  });

  const AppDescription = styled(Typography)({
    lineHeight: '16px !important',
    color: 'rgba(12, 23, 50, 0.7)',
    font: `normal normal 400 12px/18px ${theme?.Style?.font || 'Arial, sans-serif'}`,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: '-webkit-box',
    WebkitLineClamp: 2,
    WebkitBoxOrient: 'vertical',
    height: '36px',
    maxWidth: '100%',
  });

  const Title = styled.h2`
    margin: 0;
    font-weight: 500;
    color: #000;
    text-align: start;
    font: normal normal bold 24px/60px ${theme?.Style?.font || 'Arial, sans-serif'};
    @media(max-width: 600px){
      font: normal normal bold 20px/36px ${theme?.Style?.font || 'Arial, sans-serif'} !important;
    }
  `;

  const TopSectionContainer = styled.div`
    display: grid;
    justify-content: space-between;
    flex-direction: row;
    gap: 20px;
    grid-template-columns: repeat(5, 4fr);
    @media screen and (max-width: 1024px) {
      grid-template-columns: repeat(3, 2fr);
      gap: 16px;
    };
    @media screen and (max-width: 1182px) {
      grid-template-columns: repeat(4, 2fr);
      gap: 16px;
    };
    @media screen and (max-width: 768px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    };
    @media screen and (max-width: 960px) {
      grid-template-columns: repeat(3, 1fr);
      gap: 16px;
    };
    @media screen and (max-width: 425px) {
      grid-template-columns: repeat(2, 1fr);
      gap: 16px;
    }
  `;

  return(  
    <Grid width={'100%'}>
      {isError(applicationState) ? 
        (
          <Err isOpen={true}/>
        ) : (
          <Grid container sx={{ pt:{md: '28px', xs:'0px', lg:'32px'}}} justifyContent={'center'} >
            <Grid display={'flex'} justifyContent={'space-between'} flexDirection={'row'} width={'100%'} container spacing={2} sx={{ pl: {xs:"10px", md:"6%", lg:""}, pb: {xs:"12px", lg:""} }}>
              <Grid item>
                <Title>{t('applications')}</Title>
              </Grid>
              {
                isAdmin && !isLoading(applicationState) ?
                <Grid item pl={{sm:'0px !important', xs:'0px !important'}} pt={'1% !important'} pr={{sm:'10% !important', xs:'8% !important'}}>
                  <Button 
                    size={matches ? 12 : 16} 
                    bgColor={theme?.Style?.btnColor} 
                    marginTopSize={0} 
                    onClick={handleAddApplication}
                  >
                    Add Application
                  </Button>
                </Grid>
                : <></>
              }
            </Grid>
            {
              isLoading(applicationState) ? 
                <CircularProgress/>
              :               
                (
                  Array.isArray(applicationDetails) && applicationDetails.length > 0 ?
                    <TopSectionContainer>
                      {paginatedData && paginatedData.map((application: Application, index: number) => (
                        <Grid 
                          item 
                          xs={12} 
                          md={2.4} 
                          lg={3} 
                          key={application?.applicationId || `app-${index}`} 
                          display={'flex'} 
                          justifyContent={'center'} 
                          maxWidth={'100% !important'} 
                          position={'relative'}
                        >
                          <AppCard onClick={() => application.url ? window.open(application.url, '_blank') : null}> 
                              { application?.imageUrl ? (
                                <AppImage1 image={application.imageUrl} />
                                ) : (
                                  <AppImage theme={application?.typeColorHighlite}>
                                    <svg xmlns="http://www.w3.org/2000/svg" width="256" height="165" viewBox="0 0 400 257.813">
                                      <g id="Group_1033" data-name="Group 1033" transform="translate(-11100 -1176)">
                                        <rect id="AdobeStock_313642835" width="400" height="257.813" transform="translate(11100 1176)" 
                                          fill={
                                            application?.typeAbbreviation && 
                                            theme?.ApplicationPage && 
                                            theme?.ApplicationPage[application?.typeAbbreviation] && 
                                            theme?.ApplicationPage[application?.typeAbbreviation]?.primary ?
                                            theme?.ApplicationPage[application?.typeAbbreviation]?.primary : 
                                            '#ffff' }/>
                                        <path id="Intersection_11" data-name="Intersection 11" d="M180.7,236.109A344.2,344.2,0,0,0,93.182,76.7L167.62,0A453.669,453.669,0,0,1,288.375,236.109Zm-180.7,0V103.157q.906.645,1.81,1.3c.938.644,1.877,1.289,2.815,2,1.939,1.353,3.879,2.836,5.755,4.383,1.251.9,2.5,1.869,3.691,2.9.062.064.187.129.25.193,2.377,1.933,4.754,3.867,7.006,5.93,2.752,2.32,5.441,4.769,8.069,7.283s5.192,5.027,7.694,7.67l-.019.019c4.253,4.383,8.32,8.895,12.26,13.535,2.69,3.223,5.255,6.445,7.819,9.8,2.44,3.223,4.817,6.445,7.131,9.8,0,.065.063.129.126.193,3.44,5.027,6.755,10.248,9.946,15.533a258.439,258.439,0,0,1,14.512,28.552c3.315,7.476,6.193,15.146,8.758,22.944l.3.926Z" transform="translate(11100 1197.703)" 
                                          fill={
                                            application?.typeAbbreviation && 
                                            theme?.ApplicationPage && 
                                            theme?.ApplicationPage[application?.typeAbbreviation] && 
                                            theme?.ApplicationPage[application?.typeAbbreviation]?.secondary ?
                                            theme?.ApplicationPage[application?.typeAbbreviation]?.secondary : 
                                            '#ffff' }/>
                                      </g>
                                    </svg>
                                  </AppImage>
                                )
                            }
                                  {
                              isAdmin ? 
                                <Grid sx={{position: 'absolute', right: 10, top: 125, backgroundColor: '#ffff', padding: '0px', borderRadius:'12px'}}>
                                  <div onClick={(e: React.MouseEvent<HTMLDivElement>) => { e.stopPropagation(); handleOpenEditApplication(application)}} style={{display:'flex', justifyContent:'flex-end',borderRadius:'5px', position:'relative', zIndex:'1' }}>
                                      <svg xmlns="http://www.w3.org/2000/svg" width="39" height="39" viewBox="0 0 51 51">
                                        <g id="Edit" transform="translate(8 8)">
                                          <g id="Icon" transform="translate(-480 -807)">
                                            <g id="Rectangle_56" data-name="Rectangle 56" transform="translate(480 807)" fill={theme?.Style?.btnColor} stroke="#fff" stroke-width="8">
                                              <rect width="35" height="35" rx="10" stroke="none"/>
                                              <rect x="-4" y="-4" width="43" height="43" rx="14" fill="none"/>
                                            </g>
                                            <g id="Icon_metro-pencil" data-name="Icon metro-pencil" transform="translate(485.429 813.071)" fill="none">
                                              <path d="M19.3,1.928A3.023,3.023,0,0,1,22.4,4.87a2.833,2.833,0,0,1-.62,1.765L20.539,7.811,16.2,3.693l1.239-1.177A3.193,3.193,0,0,1,19.3,1.928ZM3.81,15.46l-1.239,5.3,5.576-1.177L19.61,8.694,15.272,4.576Z" stroke="none"/>
                                              <path d="M 19.29973411560059 3.428037643432617 C 18.96803283691406 3.428037643432617 18.65374755859375 3.517732620239258 18.38523864746094 3.688236236572266 L 18.38014984130859 3.69306755065918 L 20.53892517089844 5.742961883544922 L 20.65413475036621 5.633562088012695 C 20.77509117126465 5.459831237792969 20.897705078125 5.201787948608398 20.897705078125 4.869749069213867 C 20.897705078125 4.074789047241211 20.18085479736328 3.428037643432617 19.29973411560059 3.428037643432617 M 15.27237987518311 6.644100189208984 L 5.168176651000977 16.23869705200195 L 4.568668365478516 18.80041122436523 L 7.421995162963867 18.19832229614258 L 17.43114852905273 8.693988800048828 L 15.27237987518311 6.644100189208984 M 19.29973411560059 1.928037643432617 C 21.01067352294922 1.928037643432617 22.397705078125 3.245079040527344 22.397705078125 4.869749069213867 C 22.397705078125 5.532009124755859 22.16710472106934 6.143117904663086 21.77811431884766 6.634788513183594 L 20.53892517089844 7.811478614807129 L 16.20176315307617 3.69306755065918 L 17.44095420837402 2.516378402709961 C 17.95874404907227 2.147008895874023 18.60226440429688 1.928037643432617 19.29973411560059 1.928037643432617 Z M 15.27237415313721 4.575578689575195 L 19.60953521728516 8.693988800048828 L 8.147045135498047 19.5783576965332 L 2.570703506469727 20.75503921508789 L 3.809894561767578 15.45994853973389 L 15.27237415313721 4.575578689575195 Z" stroke="none" fill="#fff"/>
                                            </g>
                                          </g>
                                        </g>
                                      </svg>
                                  </div>
                                
                                </Grid>
                                :
                                <></>
                              }                   
                            <AppCardContent className={classes.cardTitle} 
                              onMouseEnter={() => handleHover(true, application.typeAbbreviation, application.applicationId, index)}
                              onMouseLeave={() => handleHoverOver(false, application.typeAbbreviation, application.applicationId, index)}>
                              <Grid container p={0} spacing={0} width={'100%'} margin={'0px'} minHeight={'80px'} maxHeight={'80px'} >
                                <Grid key={index} item xs={4} margin={'0px !important'} paddingRight={'8px'} paddingTop={'16px !important'}>
                                    {
                                      <svg width="250" height="40"
                                      style={{fill: !isHoveredPL && index === selectedCardIndex 
                                        ? application?.typeColorNormal === null ? '#1D7058' : application?.typeColorNormal 
                                        : application?.typeColorHighlite === null ? '#09B885' : application?.typeColorHighlite }}
                                      >
                                        <rect x="0" y="0" rx="6" ry="6" width="42" height="40"/>
                                        <text fill="#ffffff" fontSize="18" fontWeight="700" fontFamily={theme?.Style?.font || 'Arial, sans-serif'} textAnchor='middle' x="21" y="26">
                                          {application.typeAbbreviation || t('abbr')}
                                        </text>
                                      </svg>
                                    }
                                </Grid>
                                <Grid item pt={'16px !important'} pl={{md:'0px !important', xs:'0px !important', sm:'0px !important'}} xs={8}>
                                    <AppTitle>{application.typeName || t('unknownAppType')}</AppTitle>
                                    <AppDescription>{application.displayName || t('noDescription')}</AppDescription>
                                </Grid>
                              </Grid>
                            </AppCardContent>
                          </AppCard>
                        </Grid>
                      ))}
                    </TopSectionContainer> :
                    
                    <Grid item>
                      <Title>{t('noApplications') || "No Applications Found"}</Title>
                    </Grid>          
                )
            }
            <Grid container justifyContent={'start'} pt={2} pl={'6%'}>
              {
                !isLoading(applicationState) && pageCount > 1 ? (
                  <div>
                <Pagination
                 count={pageCount} page={currentPage} onChange={handlePageChange} />
              </div>
                ) : (
                  <></>
                )
              }              
            </Grid>
            <Grid item>
              <Drawer
                variant="temporary"
                anchor={'right'}
                open={openDrawer}
                onClose={handleDrawerClose}
                sx={{
                  display: { xs: 'flex', sm: 'block', md:'flex' },
                  '& .MuiDrawer-paper': { boxSizing: 'border-box' },
                  '& .css-i9fmh8-MuiBackdrop-root-MuiModal-backdrop' : {backgroundColor:"#000000b0"},
                  '& .css-919eu4' :{backgroundColor:"#000000b0"}
                }}
                classes={{
                  paper: clsx((!matches && !matchesTab) ? classes.drawerForm : ((matchesTab && !matches) ? classes.drawerFormTablet : classes.drawerFormMobile)), 
                  }}>
                  <EditApplicationCard 
                    equipDetails={cardDetails} 
                    handleModalClose={handleCloseEdit} 
                    reload={handleReload} 
                    newApp={createNewApp} 
                    securityGrp={securityGrp} 
                    err={handleError}
                  />
              </Drawer>
            </Grid>
          </Grid>
        )
  
       }
    </Grid>
    )
};

export default ApplicationCard